import React, { useEffect, useRef } from "react";
import "../Style/Bhero.css";
import { useLanguage } from "../Context/Languagecontext";

const BusinessHero = () => {
  const heroRef = useRef(null);
  const { t, lang } = useLanguage();
  const heroData = t.businessHero;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("in-view");
        }
      },
      { threshold: 0.1 },
    );
    if (heroRef.current) observer.observe(heroRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      className={`Business-hero ${lang === 'ta' ? 'Business-hero--tamil' : ''}`} 
      ref={heroRef}
    >
      {/* ── Ambient background layers ── */}
      <div className="bh-bg-layer bh-bg-vignette" />
      <div className="bh-bg-layer bh-bg-grain" />
      <div className="bh-bg-layer bh-bg-radial" />

      {/* ── Decorative corner ornaments ── */}
      <svg
        className="bh-ornament bh-ornament--tl"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 110 Q10 10 110 10"
          stroke="var(--clr-gold)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.5"
        />
        <path
          d="M10 90  Q10 10  90 10"
          stroke="var(--clr-gold)"
          strokeWidth="0.4"
          fill="none"
          opacity="0.3"
        />
        <circle cx="10" cy="10" r="3" fill="var(--clr-gold)" opacity="0.6" />
        <circle cx="110" cy="10" r="1.5" fill="var(--clr-gold)" opacity="0.4" />
        <circle cx="10" cy="110" r="1.5" fill="var(--clr-gold)" opacity="0.4" />
      </svg>
      <svg
        className="bh-ornament bh-ornament--tr"
        viewBox="0 0 120 120"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M110 110 Q110 10 10 10"
          stroke="var(--clr-gold)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.5"
        />
        <path
          d="M110 90  Q110 10 30 10"
          stroke="var(--clr-gold)"
          strokeWidth="0.4"
          fill="none"
          opacity="0.3"
        />
        <circle cx="110" cy="10" r="3" fill="var(--clr-gold)" opacity="0.6" />
      </svg>

      {/* ── Floating gold particles ── */}
      {[...Array(12)].map((_, i) => (
        <span key={i} className={`bh-particle bh-particle--${i + 1}`} />
      ))}

      {/* ── Hero Content ── */}
      <div className="bh-content">
        {/* Eyebrow label */}
        <div className="bh-eyebrow">
          <span className="bh-eyebrow__line" />
          <span className="bh-eyebrow__text">{heroData?.eyebrowText}</span>
          <span className="bh-eyebrow__line" />
        </div>

        {/* Main headline */}
        <h1 className="bh-title">
          {heroData?.headlineRows?.map((row, rowIndex) => (
            <span key={rowIndex} className="bh-title__row">
              {row.map((word, wordIndex) => {
                // Calculate incremental sequence order for animation targeting
                const wordOrder = rowIndex * 3 + wordIndex + 1;
                return (
                  <span
                    key={wordIndex}
                    className={`bh-word bh-word--${wordOrder} ${
                      word.isSerif ? "bh-word--serif" : ""
                    }`}
                  >
                    {word.text}
                  </span>
                );
              })}
            </span>
          ))}
        </h1>

        {/* Sub-copy */}
        <p className="bh-subtitle">{heroData?.subtitleText}</p>

        {/* Stats — single horizontal row with dividers */}
        <div className="bh-stats">
          {heroData?.stats?.map(({ num, label }, i) => (
            <React.Fragment key={label}>
              {i > 0 && <span className="bh-stat-divider" />}
              <div
                className="bh-stat"
                style={{ "--stat-delay": `${1.0 + i * 0.15}s` }}
              >
                <span className="bh-stat__num">{num}</span>
                <span className="bh-stat__label">{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── Bottom wave curve ── */}
      <div className="bh-wave-wrap">
        <svg
          className="bh-wave"
          viewBox="0 0 1440 110"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,60 C240,110 480,10 720,55 C960,100 1200,20 1440,60 L1440,110 L0,110 Z"
            fill="rgba(54,4,5,0.18)"
          />
          <path
            d="M0,65 C240,115 480,15 720,60 C960,105 1200,25 1440,65 L1440,110 L0,110 Z"
            fill="var(--clr-ivory)"
          />
          <path
            d="M0,65 C240,115 480,15 720,60 C960,105 1200,25 1440,65"
            fill="none"
            stroke="var(--clr-gold)"
            strokeWidth="1.2"
            opacity="0.55"
          />
        </svg>
      </div>
    </section>
  );
};

export default BusinessHero;